import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "environments/environment";
import { firstValueFrom } from "rxjs";

/**
 * What is in flight on a style's or a feature choice's products, in Uniformax and Proximity,
 * added up. The server's ProductExposureSummary; keep the two in step.
 */
export interface ProductExposureSummaryDTO {
  StyleCount: number;
  ProductCount: number;
  /** Used in either system: a change to what these products mean also changes those records. */
  UsedProducts: number;
  /** Uniformax never used them and Proximity was not checked: not known. */
  UnknownProducts: number;

  UniformaxUsedProducts: number;
  OwedLines: number;
  OwedQty: number;
  OwedLinesNeverOrdered: number;
  OpenPoLines: number;
  OpenPoQty: number;
  ItemsHeld: number;
  AccountOwnedItemsHeld: number;
  /** Of ItemsHeld, the units for a job order or in process: in flight, unlike shelf stock. */
  ItemsInFlight: number;

  ProximityChecked: boolean;
  ProximityError: string | null;
  StylesNotInProximity: number;
  ProximityUsedProducts: number;
  CartLines: number;
  CartQty: number;
  UnplacedLines: number;
  UnplacedQty: number;
  HistoryLines: number;
  KitLines: number;
  InventoryApprovals: number;
  CatalogEntries: number;

  /** The server refuses a meaning change on used products (gate mode Enforce). In Report it logs and saves. */
  MeaningGateEnforced: boolean;
}

/** The server's PhaseoutDrainState. */
export enum PhaseoutDrainStateEnum {
  Drained = 1,
  Draining = 2,
  CannotCheck = 3,
}

/** One Phaseout row on the drain list (gatekeeper L3); the server's PhaseoutDrainRow. */
export interface PhaseoutDrainRowDTO {
  EntityTypeName: "Product" | "ProductType" | "FeatureChoice" | "Feature" | "ManufacturerProductTypeConfig";
  EntityId: number;
  Kind: string;
  Name: string;
  ProductTypeId: number | null;
  Style: string | null;
  ManufacturerName: string | null;
  ProductCount: number;
  ProductsInFlight: number;
  OwedLines: number;
  OpenPoLines: number;
  ItemsInFlight: number;
  UnplacedLines: number;
  State: PhaseoutDrainStateEnum;
  Label: string;
  Work: string | null;
}

export interface PhaseoutDrainListDTO {
  ProximityChecked: boolean;
  ProximityError: string | null;
  Rows: PhaseoutDrainRowDTO[];
}

/** HTTP client for /api/ProductExposure. Read-only. */
@Injectable({ providedIn: "root" })
export class ProductExposureApiService {
  private readonly baseUrl = environment.apiRoot + "api/ProductExposure";

  constructor(private http: HttpClient) {}

  async getForProductType(productTypeId: number): Promise<ProductExposureSummaryDTO> {
    const params = new HttpParams().set("productTypeId", productTypeId);
    return firstValueFrom(this.http.get<ProductExposureSummaryDTO>(`${this.baseUrl}/ProductType`, { params }));
  }

  async getForFeatureChoice(featureChoiceId: number): Promise<ProductExposureSummaryDTO> {
    const params = new HttpParams().set("featureChoiceId", featureChoiceId);
    return firstValueFrom(this.http.get<ProductExposureSummaryDTO>(`${this.baseUrl}/FeatureChoice`, { params }));
  }

  async getForProducts(productIds: number[]): Promise<ProductExposureSummaryDTO> {
    return firstValueFrom(this.http.post<ProductExposureSummaryDTO>(`${this.baseUrl}/Products`, productIds));
  }

  async getPhaseoutDrain(): Promise<PhaseoutDrainListDTO> {
    return firstValueFrom(this.http.get<PhaseoutDrainListDTO>(`${this.baseUrl}/PhaseoutDrain`));
  }
}

export class ProductExposureFns {
  /**
   * Gatekeeper P3.6: the controls that change what a product means are disabled when the server
   * would refuse them - the gate enforces and a product was used. The server stays the authority:
   * an unknown (Proximity not checked) leaves the control on, and the click or the save says why.
   */
  static isMeaningLocked(use: ProductExposureSummaryDTO | null | undefined): boolean {
    return !!use && use.MeaningGateEnforced && use.UsedProducts > 0;
  }

  /** The open work as an HTML list for a confirm message, or "" when there is none. */
  static openWorkList(use: ProductExposureSummaryDTO): string {
    return ProductExposureFns.workList([
      [use.OwedLines, "job order lines to ship"],
      [use.OpenPoLines, "purchase order lines to receive"],
      [use.ItemsHeld, "units in stock or in process"],
      [use.CartLines, "Proximity cart lines"],
      [use.UnplacedLines, "Proximity order lines not placed"],
    ]);
  }

  /**
   * The work that stops Inactive (the server's Inactive gate, gatekeeper L2) as an HTML list, or ""
   * when there is none. Shelf stock and carts are not in it.
   */
  static inFlightList(use: ProductExposureSummaryDTO): string {
    return ProductExposureFns.workList([
      [use.OwedLines, "job order lines to ship"],
      [use.OpenPoLines, "purchase order lines to receive"],
      [use.ItemsInFlight, "units for job orders or in process"],
      [use.UnplacedLines, "Proximity order lines not placed"],
    ]);
  }

  private static workList(counts: [number, string][]): string {
    const lines = counts.filter(([n]) => n > 0).map(([n, what]) => `<li>${n} ${what}</li>`).join("");
    return lines ? `<ul class="list-disc ml-6 my-2">${lines}</ul>` : "";
  }

  /**
   * The Reassign Color warning. Orders, purchase orders and stock read a product's color live, so
   * a reassign on a used product changes them too. That is right when the two names are the same
   * garment and wrong when they are not, and only the operator knows which.
   * Null `use`: the count failed.
   */
  static reassignColorWarning(use: ProductExposureSummaryDTO | null): string {
    const advice = "<br/><br/>Use this only if the two names are for the same color."
      + " If the color is different, do not change it. Put the products on Phaseout.";
    if (use == null) return "<br/><br/><b>The system cannot count the orders on these products.</b>" + advice;
    if (use.UsedProducts === 0 && use.UnknownProducts === 0) return "";

    const open = ProductExposureFns.openWorkList(use);
    return (use.UsedProducts > 0
        ? `<br/><br/><b>${use.UsedProducts} of these products are on orders.</b>`
          + " Those orders, purchase orders and stock items show the new color."
        : "")
      + (open ? `<br/><br/>Open work on these products:${open}` : "")
      + (use.ProximityChecked ? "" : "<br/><br/>The system cannot check Proximity.")
      + advice;
  }
}
